import React, {FC, useState} from 'react';
import classNames from 'classnames';
import TimeSlotPicker, {TPStyle} from 'components/TimePicker';
import {Coords, DateSlot, Location, TimeSlot} from 'components/TimePicker/types';
import {Link, navigate} from 'gatsby';
import {getDistance} from 'geolib';
import {useContextLocation} from 'pages/booking/components/useLocation';
import {createBookingLink} from 'utils/booking';

import Store from './svgs/store.svg';
import Van from './svgs/truck.svg';
import Modal from './Modal';

import * as styles from './styles.module.scss';

export {default as Modal} from './Modal';

type LocationProps = {
	location: Location
	loading?: boolean

	modal?: {
		initial?: string
		onHide: () => void
	}
}

const LocationIcon: FC<{store?: boolean}> = ({store}) => {
	if (store) {
		return (
			<div className={styles.icon}>
				<img src={Store as string} /> Storefront
			</div>
		);
	}

	return (
		<div className={styles.icon}>
			<img src={Van as string} /> Mobile Clinic
		</div>
	);
};

const LocationDistance: FC<{coords: Coords}> = ({coords}) => {
	const {configs} = useContextLocation();
	if (!configs.coords) {
		return null;
	}

	const meters = getDistance(configs.coords, coords);
	const metersPerMile = 1610;
	if (meters) {
		return (
			<span className={styles.distance}>
				{(meters / metersPerMile).toFixed(1)} mi from location
			</span>
		);
	}

	return null;
};

export const LocationCard: FC<LocationProps> = ({location, loading, modal}) => {
	const [date, setDate] = useState<string>('');
	const [mref, setModal] = useState<React.ReactNode>();

	// If expand, create a modal wrapping the current
	// card and pass in the "modal" flag to customize content
	const onExpand = () => {
		const onHide = () => setModal(null);
		setModal(() => (
			<Modal onHide={onHide}>
				<LocationCard
					location={location}
					modal={{initial: date, onHide}}
				/>
			</Modal>
		));
	};

	// Simple helper function for generating the name link
	const link = () => createBookingLink({
		slug: location.details.slug,
		date: date,
	});

	// If this is a location without any dates left, return null
	if (location.datesLeft.length === 0) {
		return null;
	}

	return (
		<div className={classNames({
			[styles.card]: true,
			[styles.modal]: Boolean(modal),
		})}>
			<div className={styles.header}>
				<Link className={styles.link} to={link()}>
					{location.details.name}
				</Link>
				<div className={styles.address}>
					{location.details.address.addr1}
					, {location.details.address.addr2}
				</div>
				<div className={styles.final}>
					<LocationIcon store={location.details.isStore} />
					<LocationDistance coords={location.details.coords} />
				</div>
			</div>

			<TimeSlotPicker
				dateSlots={location.datesLeft}
				style={modal ? TPStyle.sectioned : TPStyle.condensed}
				initial={modal?.initial}
				loading={loading}
				callbacks={{
					onDate: (d: DateSlot) => {
						setDate(d.date);
					},
					onTime: (t: TimeSlot) => {
						navigate(createBookingLink({
							slug: location.details.slug,
							date: date,
							appt: t.id,
						}));
					},
				}}
			/>

			{/* If we're not in a modal, add "expand" button */}
			{/* Otherwise, add a hide button back to our parent */}
			<div className={styles.button}>
				{!modal ? (
					<button onClick={onExpand}>
						Show all time slots
					</button>
				): (
					<button onClick={modal.onHide}>
						Close
					</button>
				)}
			</div>

			{/* If we need to render a modal child, do it here */}
			{mref}
		</div>
	);
};
